import { NavLink } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAnnouncementFeed } from '@/hooks/useAnnouncements';

export const WHATS_NEW_PATH = '/nyheder';

/** The unread count in primary blue, not the bell's red: news does not need action (mockup). */
export function unreadLabel(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/**
 * Fixed row between the scrolling menu and the user block. Deliberately not a `navigation.ts` entry:
 * those rows scroll and differ per menu variant, and an unread count must be visible without
 * scrolling in every one of them (company, partner, supplier session, supplier shell).
 *
 * IDP-only by placement: it calls the .NET hooks, so the sidebar mounts it only under `idpEnabled`.
 */
export function WhatsNewSidebarRow() {
  const { unreadCount } = useAnnouncementFeed();
  return (
    <div className="border-t border-bordergray px-4 py-2 dark:border-dark-border">
      <NavLink
        to={WHATS_NEW_PATH}
        aria-label={unreadCount > 0 ? `Nyt i Mesterplan, ${unreadCount} ulæste` : 'Nyt i Mesterplan'}
        className={({ isActive }: { isActive: boolean }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-[10px] text-[14px] font-medium transition-all duration-150',
            isActive
              ? 'bg-primary text-white shadow-sm'
              : 'text-muted hover:bg-primary-50 hover:text-primary dark:text-dark-muted dark:hover:bg-dark-hover dark:hover:text-primary-400',
          )
        }
      >
        {({ isActive }: { isActive: boolean }) => (
          <>
            <Sparkles className={cn('h-[20px] w-[20px] flex-shrink-0 stroke-[1.5]', isActive && 'text-white')} />
            <span className="truncate">Nyt i Mesterplan</span>
            {unreadCount > 0 && (
              <span
                data-testid="whats-new-sidebar-badge"
                className={cn(
                  'ml-auto inline-flex h-5 min-w-[22px] items-center justify-center rounded-full px-[7px] text-[11px] font-bold',
                  isActive ? 'bg-white text-primary' : 'bg-primary text-white',
                )}
              >
                {unreadLabel(unreadCount)}
              </span>
            )}
          </>
        )}
      </NavLink>
    </div>
  );
}

/**
 * Blue dot on the burger button below 1024 px, where the sidebar (and its count) is hidden.
 * Absolutely positioned: the host button must be `relative`.
 */
export function WhatsNewMenuDot() {
  const { unreadCount } = useAnnouncementFeed();
  if (unreadCount <= 0) return null;
  return (
    <span
      data-testid="whats-new-menu-dot"
      aria-hidden="true"
      className="absolute right-[5px] top-[6px] h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-white dark:ring-dark-card"
    />
  );
}
